const DEFAULT_ADDITIONAL_COSTS = [
  { name: 'Právní služby', amount: 60000 },
  { name: 'Prodej a marketing', amount: 130000 },
]

const DEFAULTS = {
  renovationCostPerM2: 18000,
  contingencyPercent: 10,
  taxRegime: 'company-dividend',
  profitSharePercent: 100,
  loanType: 'annuity',
  loanTermMonths: 240,
  loanDrawMonth: 0,
  loanEntryFee: 0,
  holdingPeriodMonths: 12,
}

const asNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : undefined
}

const asInput = (value) => (value === undefined || value === null ? '' : String(value))
const roundedRenovatedPrice = (renovatedValue) => {
  const price = asNumber(renovatedValue?.anchorPpm2)
  return price && price > 0 ? Math.round(price) : undefined
}

export const getFeasibilityForm = (property, renovatedValue) => {
  const feasibility = property?.feasibility || {}
  const overrides = feasibility.overrides || {}
  const saleableAreaM2 = overrides.saleableAreaM2 ?? asNumber(property?.m2)
  const financing = feasibility.financing || {}
  const loan = financing.loans?.[0] || {}

  return {
    saleableAreaM2: asInput(saleableAreaM2),
    purchasePrice: asInput(overrides.purchasePrice ?? asNumber(property?.price)),
    targetPricePerM2: asInput(overrides.targetPricePerM2 ?? roundedRenovatedPrice(renovatedValue)),
    renovationAreaM2: asInput(feasibility.renovation?.areaM2 ?? saleableAreaM2),
    renovationCostPerM2: asInput(feasibility.renovation?.costPerM2 ?? DEFAULTS.renovationCostPerM2),
    contingencyPercent: asInput(feasibility.assumptions?.contingencyPercent ?? DEFAULTS.contingencyPercent),
    taxRegime: feasibility.assumptions?.taxRegime ?? DEFAULTS.taxRegime,
    profitSharePercent: asInput(feasibility.assumptions?.profitSharePercent ?? DEFAULTS.profitSharePercent),
    additionalCosts: (feasibility.additionalCosts?.length ? feasibility.additionalCosts : DEFAULT_ADDITIONAL_COSTS).map((cost) => ({
      name: cost.name,
      amount: asInput(cost.amount),
    })),
    financingEnabled: Boolean(financing.loans?.length),
    loanType: loan.type ?? DEFAULTS.loanType,
    loanPrincipal: asInput(loan.principal),
    loanAnnualRatePercent: asInput(loan.annualRatePercent),
    loanTermMonths: asInput(loan.termMonths ?? DEFAULTS.loanTermMonths),
    loanDrawMonth: asInput(loan.drawMonth ?? DEFAULTS.loanDrawMonth),
    loanEntryFee: asInput(loan.entryFee ?? DEFAULTS.loanEntryFee),
    holdingPeriodMonths: asInput(financing.holdingPeriodMonths ?? DEFAULTS.holdingPeriodMonths),
  }
}

const cleanAdditionalCosts = (costs) => {
  const normalized = (costs || [])
    .filter((cost) => cost.name.trim() || cost.amount !== '')
    .map((cost) => ({ name: cost.name.trim(), amount: asNumber(cost.amount) }))
  if (normalized.some((cost) => !cost.name || !(cost.amount >= 0))) throw new Error('Každý dodatečný náklad musí mít název a nezápornou částku.')
  return normalized
}

export const prepareFeasibilityCalculation = (property, renovatedValue, form) => {
  const saleableAreaM2 = asNumber(form.saleableAreaM2)
  const purchasePrice = asNumber(form.purchasePrice)
  const targetPricePerM2 = asNumber(form.targetPricePerM2)
  const renovationAreaM2 = asNumber(form.renovationAreaM2)
  const renovationCostPerM2 = asNumber(form.renovationCostPerM2)
  const contingencyPercent = asNumber(form.contingencyPercent)
  const profitSharePercent = asNumber(form.profitSharePercent)
  if (!(saleableAreaM2 > 0) || !(purchasePrice > 0) || !(targetPricePerM2 > 0)) {
    throw new Error('Doplňte prodejní plochu, kupní cenu a cílovou prodejní cenu za m².')
  }
  if (!(renovationAreaM2 > 0) || !(renovationCostPerM2 >= 0) || !(contingencyPercent >= 0 && contingencyPercent <= 100) || !(profitSharePercent >= 0 && profitSharePercent <= 100)) {
    throw new Error('Zkontrolujte výměru rekonstrukce, náklady, rezervu a podíl na zisku.')
  }

  const additionalCosts = cleanAdditionalCosts(form.additionalCosts)
  let financing
  if (form.financingEnabled) {
    const principal = asNumber(form.loanPrincipal)
    const annualRatePercent = asNumber(form.loanAnnualRatePercent)
    const termMonths = asNumber(form.loanTermMonths)
    const holdingPeriodMonths = asNumber(form.holdingPeriodMonths)
    if (!(principal > 0) || !(annualRatePercent >= 0) || !(termMonths > 0) || !(holdingPeriodMonths > 0)) {
      throw new Error('Doplňte jistinu, sazbu, délku úvěru a dobu držení.')
    }
    financing = {
      holdingPeriodMonths,
      loans: [{
        type: form.loanType,
        principal,
        annualRatePercent,
        termMonths,
        drawMonth: asNumber(form.loanDrawMonth) ?? 0,
        entryFee: asNumber(form.loanEntryFee) ?? 0,
      }],
    }
  }

  const listingAreaM2 = asNumber(property?.m2)
  const listingPrice = asNumber(property?.price)
  const valuationTargetPricePerM2 = roundedRenovatedPrice(renovatedValue)
  const overrides = {}
  if (saleableAreaM2 !== listingAreaM2) overrides.saleableAreaM2 = saleableAreaM2
  if (purchasePrice !== listingPrice) overrides.purchasePrice = purchasePrice
  if (targetPricePerM2 !== valuationTargetPricePerM2) overrides.targetPricePerM2 = targetPricePerM2

  const renovation = { costPerM2: renovationCostPerM2 }
  if (renovationAreaM2 !== saleableAreaM2) renovation.areaM2 = renovationAreaM2
  const config = {
    active: true,
    overrides,
    renovation,
    additionalCosts,
    assumptions: { contingencyPercent, taxRegime: form.taxRegime, profitSharePercent },
    ...(financing && { financing }),
  }
  const previewPayload = {
    source: {
      system: 'realitni-jestrab',
      propertyId: String(property?.propertyId || ''),
      url: property?.link || '',
      title: property?.title || '',
      location: property?.location || '',
      retrievedAt: new Date().toISOString(),
    },
    currency: 'CZK',
    property: { saleableAreaM2, purchasePrice },
    sale: { targetPricePerM2 },
    renovation: { areaM2: renovationAreaM2, costPerM2: renovationCostPerM2 },
    additionalCosts,
    assumptions: config.assumptions,
    ...(financing && { financing }),
  }

  return { config, previewPayload }
}

export const formatFeasibilityMoney = (value) => (Number.isFinite(Number(value)) ? `CZK ${Math.round(Number(value)).toLocaleString('en-US')}` : '—')
